import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { ApiError, apiClient } from "../api/client";
import type { MeResponse } from "../api/types";

type AuthStatus = "loading" | "authenticated" | "unauthenticated" | "forbidden" | "error";

interface AuthContextValue {
  status: AuthStatus;
  me: MeResponse | null;
  error: string | null;
  login: () => void;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [me, setMe] = useState<MeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const nextMe = await apiClient.getMe();
      setMe(nextMe);
      setStatus("authenticated");
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        setMe(null);
        setStatus("unauthenticated");
        return;
      }
      if (caught instanceof ApiError && caught.status === 403) {
        setMe(null);
        setStatus("forbidden");
        return;
      }
      if (caught instanceof ApiError && caught.status === 503) {
        setMe(null);
        setStatus("error");
        setError("Service is temporarily unavailable — please retry.");
        return;
      }
      setMe(null);
      setStatus("error");
      setError(caught instanceof Error ? caught.message : "Unable to load your session.");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(() => {
    window.location.assign("/auth/login");
  }, []);

  const logout = useCallback(async () => {
    setError(null);
    try {
      const response = await apiClient.logout();
      setMe(null);
      setStatus("unauthenticated");
      if (response.logout_url !== undefined && response.logout_url !== "") {
        window.location.assign(response.logout_url);
      }
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        setMe(null);
        setStatus("unauthenticated");
        return;
      }
      setError(caught instanceof Error ? caught.message : "Unable to log out.");
      throw caught;
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      me,
      error,
      login,
      logout,
      refresh
    }),
    [error, login, logout, me, refresh, status]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === undefined) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return value;
}
